import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import _ from 'lodash';
import { StatusCodes } from 'http-status-codes';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import NutrientService from '~/src/services/logic/NutrientService';
import { IngredientGetDto } from '~/src/types/ingredientGetDto';
import IngredientsApi from '~/src/services/api/IngredientsApi';
import { splitNoParen } from '~/src/utils/misc';
import { DEFAULT_SERVINGS, DEFAULT_SERVING_SIZE } from '~/src/utils/constants';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import TitleComponent from '~/src/components/common/forms/Title/TitleComponent';
import UsdaApi from '~/src/services/api/UsdaApi';
import UsdaResultBox from './UsdaResultBox';
import { NutriComponentBox } from './NutriComponentBox';
import { NutritionSetEditDto } from '~/src/types/nutritionSetEditDto';
import DbResultBox from './DbResultBox';
import { Colors } from '~/src/scss/common/style';
import infoIcon from '../../../static/img/infoIcon.svg';
import searchWhite from '../../../static/img/searchWhite.svg';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import TextAreaComponent from '~/src/components/common/forms/TextInput/TextAreaComponent';

const nutrientSchema = yup.object().shape({
  calories: yup.number(),
  fat: yup.number(),
  saturatedFat: yup.number(),
  transFat: yup.number(),
  cholesterol: yup.number(),
  sodium: yup.number(),
  carbohydrates: yup.number(),
  fiber: yup.number(),
  sugars: yup.number(),
  addedSugars: yup.number(),
  protein: yup.number(),
});

interface Props {
  ingredient: IngredientGetDto;
  isParent?: boolean;
  setIngredient: (ingredient: IngredientGetDto) => void;
}

export default function NutritionLabelAndSearch({
  ingredient,
  setIngredient,
  isParent = false,
}: Props): JSX.Element {
  const [usdaResults, setUsdaResults] = useState<SearchResultFood[]>([]);
  const [dbResults, setDbResults] = useState<IngredientGetDto[]>([]);

  const history = useHistory();
  const nutriFactsFormik = useFormik({
    initialValues: NutrientService.null2emptyStrings(ingredient.nutritionSet) as NutritionSetEditDto,
    onSubmit: (values) => {
      fetchData(values, nutriFactsFormik.setSubmitting);
    },
    validationSchema: nutrientSchema,
  });

  const searchFormik = useFormik({
    initialValues: {
      query: ingredient.fdcName ?? ingredient.name,
      brandName: '',
      brandOwner: '',
    },
    onSubmit: (values) => {
      fetchData(values, searchFormik.setSubmitting);
    },
  });

  const ingredientsFormik = useFormik({
    initialValues: {
      ingredients: ingredient.ingredientsText,
      lessThan: ingredient.lessThan ?? '',
      moreThan: ingredient.moreThan ?? '',
      servings: ingredient.servings ?? '',
      servingSize: ingredient.servingSize ?? '',
    },
    onSubmit: (values) => {
      // fetchData(values, searchFormik.setSubmitting);
    },
  });

  const fetchData = async (values: SearchFoodQuery, setSubmitting) => {
    setSubmitting(true);
    let res;
    if (isParent) {
      res = await UsdaApi.searchProduct(values);
    } else {
      res = await UsdaApi.searchFood(values);
    }

    const res2 = await IngredientsApi.search(values.query);

    if (res.status === StatusCodes.OK) {
      setUsdaResults(res.data.foods);
    }
    if (res2.status === StatusCodes.OK) {
      setDbResults(res2.data.data);
    }
    setSubmitting(false);
  };

  const setNutriFacts = (food: IngredientGetDto & SearchResultFood): void => {
    const calories =
      food?.nutritionSet?.calories ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1008)?.value ?? 0;
    const fat = food?.nutritionSet?.fat ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1004)?.value ?? 0;
    const saturatedFat =
      food?.nutritionSet?.saturatedFat ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1258)?.value ?? 0;
    const transFat =
      food?.nutritionSet?.transFat ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1257)?.value ?? 0;
    const cholesterol =
      food?.nutritionSet?.cholesterol ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1253)?.value ?? 0;
    const sodium =
      food?.nutritionSet?.sodium ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1093)?.value ?? 0;
    const carbohydrates =
      food?.nutritionSet?.carbohydrates ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1005)?.value ?? 0;
    const fiber =
      food?.nutritionSet?.fiber ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1079)?.value ?? 0;
    const sugars =
      food?.nutritionSet?.sugars ?? food?.foodNutrients?.find((n) => n?.nutrientId === 2000)?.value ?? 0;
    const addedSugars = food?.nutritionSet?.addedSugars ?? 0;
    const protein =
      food?.nutritionSet?.protein ?? food?.foodNutrients?.find((n) => n?.nutrientId === 1003)?.value ?? 0;

    const nutrients = {
      calories,
      fat,
      saturatedFat,
      transFat,
      cholesterol,
      sodium,
      carbohydrates,
      fiber,
      sugars,
      addedSugars,
      protein,
    };

    nutriFactsFormik.setValues(nutrients);
    ingredientsFormik.setValues({
      ingredients: food.ingredients,
      servings: DEFAULT_SERVINGS,
      servingSize: DEFAULT_SERVING_SIZE,
    });

    const ingredientCopy = _.cloneDeep(ingredient);
    ingredientCopy.nutritionSet = nutrients;
    ingredientCopy.ingredientsText = food.ingredients ?? '';
    ingredientCopy.source = 'fdc api';
    ingredientCopy.fdcId = food.fdcId;
    ingredientCopy.fdcName = food.description;
    ingredientCopy.servings = DEFAULT_SERVINGS;
    ingredientCopy.servingSize = DEFAULT_SERVING_SIZE;
    setIngredient(ingredientCopy);
  };

  const handleBlur = async (): Promise<void> => {
    const ingredientCopy = _.cloneDeep(ingredient);
    ingredientCopy.nutritionSet = nutriFactsFormik.values;
    ingredientCopy.lessThan = ingredientsFormik.values.lessThan;
    ingredientCopy.moreThan = ingredientsFormik.values.moreThan;
    ingredientCopy.ingredientsText = ingredientsFormik.values.ingredients;
    ingredientCopy.servings = ingredientsFormik.values.servings;
    ingredientCopy.servingSize = ingredientsFormik.values.servingSize;
    setIngredient(ingredientCopy);
  };

  const processIngredients = async (): Promise<void> => {
    // For Processing
    const splitted = splitNoParen(ingredient.ingredientsText);
    const trimmed = splitted.map((item) => item.trim());
    const res = await IngredientsApi.processIngredients(ingredient.id, { ingredients: trimmed });
    if (res.status === StatusCodes.OK) {
      const copy = _.cloneDeep(ingredient);
      copy.childIngredients = res.data;
      setIngredient(copy);
    }
  };

  const saveToServer = async (): Promise<void> => {
    const res = await IngredientsApi.updateIngredient(ingredient.id, ingredient);
  };

  useEffect(() => {
    saveToServer();
  }, [ingredient]);

  return (
    <>
      <Row>
        <Col>
          <>
            <LabelContainer>
              <TitleComponent title="Nutrition Facts" Heading="h2" />
              <NutriComponentBox
                handleBlur={(e) => {
                  handleBlur();
                  ingredientsFormik.handleBlur(e);
                }}
                handleChange={ingredientsFormik.handleChange}
                leftText="Servings"
                leftBold={true}
                hrThickness={0}
                inputValue={ingredientsFormik.values.servings}
                name="servings"
                placeholder={`default: ${DEFAULT_SERVINGS}`}
              />
              <NutriComponentBox
                handleBlur={(e) => {
                  handleBlur();
                  ingredientsFormik.handleBlur(e);
                }}
                handleChange={ingredientsFormik.handleChange}
                leftText="Serving size"
                unit="g"
                leftBold={true}
                hrThickness={1}
                inputValue={ingredientsFormik.values.servingSize}
                name="servingSize"
                placeholder={`default: ${DEFAULT_SERVING_SIZE}`}
              />
              {/* <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Amount per serving"
                leftBold={true}
                hrThickness={0}
              /> */}
              <StyleP>Amount per serving</StyleP>
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Calories"
                unit="g"
                leftBold={true}
                hrThickness={1}
                inputValue={nutriFactsFormik.values.calories}
                name="calories"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Total Fat"
                unit="g"
                leftBold={true}
                inputValue={nutriFactsFormik.values.fat}
                name="fat"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Saturated Fat"
                unit="g"
                indent={1}
                inputValue={nutriFactsFormik.values.saturatedFat}
                name="saturatedFat"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Trans Fat"
                unit="g"
                indent={1}
                inputValue={nutriFactsFormik.values.transFat}
                name="transFat"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Cholesterol"
                unit="mg"
                leftBold={true}
                inputValue={nutriFactsFormik.values.cholesterol}
                name="cholesterol"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Sodium"
                unit="mg"
                leftBold={true}
                inputValue={nutriFactsFormik.values.sodium}
                name="sodium"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Total Carbohydrate"
                unit="g"
                leftBold={true}
                inputValue={nutriFactsFormik.values.carbohydrates}
                name="carbohydrates"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Dietary Fiber"
                unit="g"
                indent={1}
                inputValue={nutriFactsFormik.values.fiber}
                name="fiber"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Total Sugars"
                unit="g"
                indent={1}
                inputValue={nutriFactsFormik.values.sugars}
                name="sugars"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Includes Added Sugars"
                unit="g"
                indent={2}
                inputValue={nutriFactsFormik.values.addedSugars}
                name="addedSugars"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Protein"
                unit="g"
                leftBold={true}
                hrThickness={1}
                inputValue={nutriFactsFormik.values.protein}
                name="protein"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Vitamin D 2"
                unit="mcg"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Calcium 260"
                unit="mg"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Iron 8"
                unit="mg"
              />
              <NutriComponentBox
                handleBlur={(e): void => {
                  handleBlur();
                  nutriFactsFormik.handleBlur(e);
                }}
                handleChange={nutriFactsFormik.handleChange}
                leftText="Potassium 235"
                unit="mg"
              />
            </LabelContainer>
            {isParent && (
              <div>
                <TextAreaComponent
                  name="ingredients"
                  onChange={ingredientsFormik.handleChange}
                  onBlur={(e) => {
                    ingredientsFormik.handleBlur(e);
                    handleBlur();
                  }}
                  value={ingredientsFormik.values.ingredients}
                  errorMessage={ingredientsFormik.errors.ingredients}
                  cols={12}
                  rows={8}
                />
                <PrimaryButton title="Process Ingredients" type="button" onClick={processIngredients} />
              </div>
            )}
          </>
        </Col>
        <Col style={{ height: 800, overflow: 'auto' }}>
          {!isParent && (
            <>
              <TextInputComponent
                inline
                type="number"
                name="moreThan"
                label="Min:"
                onChange={ingredientsFormik.handleChange}
                onBlur={(e): void => {
                  handleBlur();
                  ingredientsFormik.handleBlur(e);
                }}
                value={ingredientsFormik.values.moreThan}
                min={0}
                inlineStyles={{ marginRight: 6, marginBottom: 12 }}
              />
              <TextInputComponent
                inline
                type="number"
                name="lessThan"
                label="Max:"
                onChange={ingredientsFormik.handleChange}
                onBlur={(e): void => {
                  handleBlur();
                  ingredientsFormik.handleBlur(e);
                }}
                value={ingredientsFormik.values.lessThan}
                min={1}
                inlineStyles={{ marginRight: 6, marginBottom: 12 }}
              />
            </>
          )}
          <WrapperForm onSubmit={searchFormik.handleSubmit}>
            <form>
              <Row>
                <Title>Search</Title>
              </Row>
              <Row style={{ padding: 7 }}>
                <Col>
                  <TextInputComponent
                    name="query"
                    label="Search Product or ingredient"
                    onChange={searchFormik.handleChange}
                    value={searchFormik.values.query}
                    cols={12}
                  />
                </Col>
                {isParent && (
                  <Col>
                    <TextInputComponent
                      name="brandOwner"
                      label="Brand Owner"
                      onChange={searchFormik.handleChange}
                      cols={12}
                      height="25"
                      value={searchFormik.values.brandOwner}
                    />
                  </Col>
                )}
              </Row>
              <PrimaryButton title="Search" type="submit" isSubmitting={searchFormik.isSubmitting} />
              <a href="https://fdc.nal.usda.gov/help.html#bkmk-4">Help</a>
              <BorderlessButton
                icon={infoIcon}
                onClick={(): void => {
                  history.push('https://fdc.nal.usda.gov/help.html#bkmk-4');
                }}
              />
            </form>
          </WrapperForm>
          <Row style={{ marginTop: 12 }}>
            {usdaResults.length > 0 && (
              <Col>
                <b>USDA Database</b>
                {usdaResults.map((food) => (
                  <UsdaResultBox food={food} key={food.fdcId} setNutriFacts={setNutriFacts} />
                ))}
              </Col>
            )}
            {dbResults.length > 0 && (
              <Col>
                <b>Svila Database</b>
                {dbResults.map((food) => (
                  <DbResultBox food={food} key={food.fdcId} setNutriFacts={setNutriFacts} />
                ))}
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </>
  );
}

const StyleP = styled.p`
  padding: 0;
  color: ${Colors.darkBrown};
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  width: 100%;
`;

const Title = styled.h1`
  padding: 0;
  color: ${Colors.darkBrown};
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  width: 100%;
`;

const LabelContainer = styled.div`
  background: #f4f4f4;
  border-radius: 10px;
`;

const WrapperForm = styled.div`
  width: 100%;
  margin-bottom: 25px;
  form {
    width: 100%;
    background: ${Colors.newOrage};
    padding: 42px;
    border: 2px solid ${Colors.newOrage};
    div {
      width: 100%;
    }
    border-radius: 10px;
  }
`;
